import { httpClient } from './httpClient';

// ============================================
// INTERFACES - Types
// ============================================

export type LegalDocumentType = 'terms_and_conditions' | 'privacy_policy';

export interface LegalDocument {
  id: number;
  type: LegalDocumentType;
  title: string;
  content: string; // HTML
  version: string;
  is_active: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateLegalDocumentData {
  type: LegalDocumentType;
  title: string;
  content: string;
  version: string;
}

export interface UpdateLegalDocumentData {
  title?: string;
  content?: string;
  version?: string;
}

export interface LegalDocumentFilters {
  type?: LegalDocumentType;
  is_active?: boolean;
  per_page?: number;
}

export interface LegalDocumentResponse {
  success: boolean;
  data: LegalDocument;
  message?: string;
}

export interface LegalDocumentsResponse {
  success: boolean;
  data: LegalDocument[];
  meta?: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

// ============================================
// SERVICE CLASS
// ============================================

class LegalDocumentService {
  private static instance: LegalDocumentService;

  static getInstance(): LegalDocumentService {
    if (!LegalDocumentService.instance) {
      LegalDocumentService.instance = new LegalDocumentService();
    }
    return LegalDocumentService.instance;
  }

  /**
   * Get active document by type (public endpoint)
   */
  async getActiveDocument(type: LegalDocumentType): Promise<LegalDocument> {
    try {
      const response = await httpClient.get<LegalDocumentResponse | LegalDocument>(
        `/api/legal-documents/active/${type}`
      );

      // Handle both response formats: {success, data: {}} or direct {}
      if ('data' in response && response.data) {
        return response.data;
      }

      return response as LegalDocument;
    } catch (error) {
      console.error(`Error fetching active ${type}:`, error);
      throw error;
    }
  }

  /**
   * Get all legal documents (admin)
   */
  async getAllDocuments(filters?: LegalDocumentFilters): Promise<LegalDocument[]> {
    try {
      const params: Record<string, string | number | boolean> = {};

      if (filters?.type) params.type = filters.type;
      if (filters?.is_active !== undefined) params.is_active = filters.is_active;
      if (filters?.per_page) params.per_page = filters.per_page;

      const response = await httpClient.get<LegalDocumentsResponse | LegalDocument[]>(
        '/api/legal-documents',
        { params }
      );

      // Handle both response formats: {success, data: []} or direct []
      if (Array.isArray(response)) {
        return response;
      }

      return response.data || [];
    } catch (error) {
      console.error('Error fetching legal documents:', error);
      throw error;
    }
  }

  /**
   * Get single document by ID (admin)
   */
  async getDocument(id: number): Promise<LegalDocument> {
    try {
      const response = await httpClient.get<LegalDocumentResponse | LegalDocument>(
        `/api/legal-documents/${id}`
      );

      // Handle both response formats: {success, data: {}} or direct {}
      if ('data' in response && response.data) {
        return response.data;
      }

      return response as LegalDocument;
    } catch (error) {
      console.error(`Error fetching legal document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create new legal document (admin)
   */
  async createDocument(data: CreateLegalDocumentData): Promise<LegalDocument> {
    try {
      const response = await httpClient.post<LegalDocumentResponse | LegalDocument>(
        '/api/legal-documents',
        data
      );

      // Handle both response formats: {success, data: {}} or direct {}
      if ('data' in response && response.data) {
        return response.data;
      }

      return response as LegalDocument;
    } catch (error) {
      console.error('Error creating legal document:', error);
      throw error;
    }
  }

  /**
   * Update legal document (admin)
   */
  async updateDocument(id: number, data: UpdateLegalDocumentData): Promise<LegalDocument> {
    try {
      const response = await httpClient.put<LegalDocumentResponse | LegalDocument>(
        `/api/legal-documents/${id}`,
        data
      );

      // Handle both response formats: {success, data: {}} or direct {}
      if ('data' in response && response.data) {
        return response.data;
      }

      return response as LegalDocument;
    } catch (error) {
      console.error(`Error updating legal document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete legal document (admin)
   */
  async deleteDocument(id: number): Promise<void> {
    try {
      await httpClient.delete(`/api/legal-documents/${id}`);
    } catch (error) {
      console.error(`Error deleting legal document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Publish legal document (admin)
   * Makes document active and deactivates previous active document of same type
   */
  async publishDocument(id: number): Promise<LegalDocument> {
    try {
      const response = await httpClient.patch<LegalDocumentResponse | LegalDocument>(
        `/api/legal-documents/${id}/publish`
      );

      // Handle both response formats: {success, data: {}} or direct {}
      if ('data' in response && response.data) {
        return response.data;
      }

      return response as LegalDocument;
    } catch (error) {
      console.error(`Error publishing legal document ${id}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const legalDocumentService = LegalDocumentService.getInstance();
